import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table } from 'antd';
import { Link } from 'react-router-dom';
import api from '../api';
import { formatTS, TS_12H } from '../utils/time';
import { ticketKey } from './HelpdeskInvolvedTickets';
import { HdStatusTag, useHelpdeskStatuses } from './HelpdeskStatus';

// Help Desk tickets for a client or a client contact, newest first, on the
// client and contact pages. Only tickets this user can see (the Help Desk's
// own rules apply). Column filters on Status, Request Type, Campaign, Queue.
//   params: { client_ids } or { client_contact_ids }

const filtersOf = (rows, key) => [...new Set(rows.map((r) => r[key]).filter(Boolean))]
  .sort().map((v) => ({ text: v, value: v }));

export default function ClientTicketsCard({ params, showContact = true }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const statuses = useHelpdeskStatuses();
  const key = JSON.stringify(params);

  useEffect(() => {
    setLoading(true);
    api.get('/api/helpdesk', { params })
      .then((r) => setRows((r.data || []).slice().sort((a, b) => String(b.CreatedAt).localeCompare(String(a.CreatedAt)))))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(() => [
    { title: 'Ticket', dataIndex: 'ID', key: 'ID', width: 100,
      render: (v, r) => <Link to={`/helpdesk/${v}`}>{ticketKey(r)}</Link> },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 160,
      filters: statuses.map((s) => ({ text: s.name, value: s.key })),
      onFilter: (v, r) => r.Status === v,
      render: (v) => <HdStatusTag status={v} /> },
    { title: 'Subject', dataIndex: 'Subject', key: 'Subject', width: 260, ellipsis: true },
    { title: 'Request Type', dataIndex: 'FormName', key: 'FormName', width: 160, render: (v) => v || '—',
      filters: filtersOf(rows, 'FormName'), onFilter: (v, r) => r.FormName === v },
    ...(showContact ? [{ title: 'Client Contact', dataIndex: 'ClientContactName', key: 'ClientContactName', width: 160,
      render: (v) => v || '—' }] : []),
    ...(!showContact ? [{ title: 'Client', dataIndex: 'ClientName', key: 'ClientName', width: 160,
      render: (v) => v || '—', filters: filtersOf(rows, 'ClientName'), onFilter: (v, r) => r.ClientName === v }] : []),
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 130, render: (v) => v || '—',
      filters: filtersOf(rows, 'CampaignName'), onFilter: (v, r) => r.CampaignName === v },
    { title: 'Queue', dataIndex: 'QueueName', key: 'QueueName', width: 130, render: (v) => v || '—',
      filters: filtersOf(rows, 'QueueName'), onFilter: (v, r) => r.QueueName === v },
    { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 150, render: (v) => v || '—' },
    { title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 170, render: (v) => formatTS(v, TS_12H),
      sorter: (a, b) => String(a.CreatedAt).localeCompare(String(b.CreatedAt)), defaultSortOrder: 'descend' },
  ], [rows, showContact, statuses]);

  return (
    <Card title={`Help Desk Tickets (${rows.length})`} size="small" style={{ marginBottom: 16 }}>
      <Table
        rowKey="ID"
        dataSource={rows}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 10, hideOnSinglePage: true, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No Help Desk tickets.' }}
        columns={columns}
      />
    </Card>
  );
}
